/**
 * Shared filtering rules for the Proposals and Estimates list pages.
 *
 * These live here rather than inline in the pages so they can be tested on their
 * own — the pages themselves need a router, the auth store and the API before
 * they will render at all.
 */
import { revisionCount } from './revision-label'

/**
 * Has this proposal been revised?
 *
 * A revision is not a status. Finalising one bumps `revisionNumber` (the Rev badge
 * in the list) and resets the proposal to draft so it has to be re-sent, so a
 * revised proposal can be sitting in any status at all — including Ready to Send,
 * which is where it lands the moment the revision is finished. The Revised tab
 * therefore cuts across the status tabs instead of being one of them: a revised
 * proposal that has been sent appears under both Sent and Revised.
 *
 * A proposal someone is revising RIGHT NOW is deliberately not counted until that
 * revision is finished — its current version is still the one the customer has.
 */
export function isRevisedProposal(record: { revisionNumber?: number | null }): boolean {
  return revisionCount(record.revisionNumber) > 0
}

export interface EstimateSearchFields {
  title?: string
  clientName?: string | null
  clientCompany?: string | null
  createdByUser?: { displayName?: string | null } | null
  /**
   * The bid's number, carried by EVERY version of it. A version a revision has
   * replaced no longer links to the proposal, so the server resolves the number
   * for it — see withVersionContext. Reading it off the live link alone found
   * only the current row and left the older versions unfindable by number.
   */
  proposalNumber?: string | null
}

/**
 * Does this estimate match what was typed in the search box?
 *
 * Matches the estimate name, the client (contact or company), the estimator who
 * created it, and the number of the proposal it belongs to — the same fields the
 * Proposals page searches, so a number typed on either page finds its job.
 * Reported from the field: someone searched Estimates for a proposal number and
 * got nothing back.
 *
 * An estimator only ever sees their own estimates (the server filters by role
 * before this runs), so for them the estimator match never narrows anything.
 */
export function matchesEstimateSearch(estimate: EstimateSearchFields, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    (estimate.title || '').toLowerCase().includes(q) ||
    (estimate.clientName || '').toLowerCase().includes(q) ||
    (estimate.clientCompany || '').toLowerCase().includes(q) ||
    (estimate.createdByUser?.displayName || '').toLowerCase().includes(q) ||
    (estimate.proposalNumber || '').toLowerCase().includes(q)
  )
}
